import Vue from "vue";
import VueRouter from "vue-router";
import ErrorPage from "@/common/pages/Error.vue";
import {PAGE_NAME, PERMISSIONS} from "@/common/utils/constants.js";
import {EventBusNavbar, EventCurdSideMenu} from "@/common/configurations/event-bus.js";
import store from "../store/index.js";
import Landing from "@/common/pages/landing/Landing.vue";
import ManagementContentPanel from "@/drc/pages/controlPanel/manageLabels/ManagementContentPanel";
import LandingPagePanel from "@/drc/pages/controlPanel/landingPage/LandingPagePanel";
import UsersManagementPanel from "@/drc/pages/controlPanel/users/UsersManagement";

import Login from "../pages/login/Login.vue";
import RegisterFrom from "@/common/pages/login/RegisterForm";
import LoginOptions from "@/common/pages/login/LoginOptions";
import GuestLogin from "@/common/pages/login/GuestLogin";
import ForgotPassword from "@/common/pages/login/ForgotPassword";
import ResetPassword from "@/common/pages/login/ResetPassword";
import ConfirmationForgotPassword from "@/common/pages/login/ConfirmationForgotPassword";
import SuccessRegistration from "@/common/pages/login/SuccessRegistration";
import Homepage from "@/drc/pages/home/Homepage.vue";

Vue.use(VueRouter);

const routes = [
	{
		path: "/",
		redirect: `/login`
	},
	{
		path: "/landing",
		component: Landing,
	},
	{
		path: "/" + PAGE_NAME.home,
		name: PAGE_NAME.home,
		component: Homepage,
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark',
			hasGlobalSearch: true
		}
	},
	{
		path: '/reset-password/:password/:expirationPeriod/:token',
		name: 'reset-password',
		component: ResetPassword,
	},
	{
		path: "/",
		name: 'login',
		component: Login,
		children: [
			{
				path: '/login',
				name: 'login-options',
				component: LoginOptions,
			},
			{
				path: 'sign-in',
				name: 'sign-in',
				component: GuestLogin,
			},
			{
				path: 'forgot-password',
				name: 'forgot-password',
				component: ForgotPassword,
			},
			{
				path: 'forgot-password/confirmation',
				name: 'confirmation',
				component: ConfirmationForgotPassword,
			},
			{
				path: 'register',
				name: 'register',
				component: RegisterFrom,
			},
			{
				path: 'register/success',
				name: 'register-success',
				component: SuccessRegistration,
			}
		]
	},

	// ------------------------------------ Search Page
	{
		path: `/${PAGE_NAME.projects}/old-${PAGE_NAME.search}`,
		name: `old-search`,
		component: () => import("@/drc/pages/search/SearchPage"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess]
		}
	},

	{
		path: `/${PAGE_NAME.projects}/${PAGE_NAME.search}`,
		name: `${PAGE_NAME.search}`,
		component: () => import("@/drc/pages/search/SearchingPage"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark',
			theme: 'dark'
		}
	},

	// ------------------------------------ Projects
	{
		path: `/${PAGE_NAME.projects}/:publishedYear/:publishedMonth/:id`,
		component: () => import("@/drc/pages/project/Project"),
		name: PAGE_NAME.singleCSWithDates,
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark'
		}
	},
	{
		path: `/${PAGE_NAME.projects}/:id`,
		name: PAGE_NAME.singleCS,
		component: () => import("@/drc/pages/project/Project"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark'
		}
	},

	// ------------------------------------ Projects Management
	{
		path: `/${PAGE_NAME.manage}/${PAGE_NAME.content}`,
		name: `manage-content`,
		component: () => import("@/drc/pages/controlPanel/ManagementPanel"),
		meta: {
			permissions: [PERMISSIONS.additionalCmtAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark',
			backgroundColor: 'blue',
			theme: 'light'
		},
	},

	{
		path: '/control-panel',
		name: 'controlPanel',
		component: () => import('@/drc/pages/controlPanel/ControlPanel'),
		children: [
			{
				path: 'landing-page',
				name: 'controlPanelLandingPage',
				component: LandingPagePanel,
				meta:{
					headerColor: 'dark',
					sideMenuColor: 'dark',
					backgroundColor: 'blue',
					theme: 'light'
				}
			},
			{
				path: 'manage-labels',
				name: 'controlPanelManageLabels',
				component: ManagementContentPanel,
				meta:{
					headerColor: 'dark',
					sideMenuColor: 'dark',
					backgroundColor: 'blue',
					theme: 'light'
				}
			},
			{
				path: 'users-management',
				name: 'usersManagement',
				component: UsersManagementPanel,
				meta:{
					headerColor: 'dark',
					sideMenuColor: 'dark',
					backgroundColor: 'blue',
					theme: 'light'
				}
			},
			{
				path: 'days-off',
				name: 'daysOff',
				component: () => import('@/drc/pages/controlPanel/days-off/DaysOff'),
				meta:{
					headerColor: 'dark',
					sideMenuColor: 'dark',
					backgroundColor: 'blue',
					theme: 'light'
				}
			},
			{
				path: 'survey-management',
				name: 'surveyManagement',
				component: () => import('@/drc/pages/controlPanel/survey-management/SurveyManagement'),
				meta:{
					headerColor: 'dark',
					sideMenuColor: 'dark',
					backgroundColor: 'blue',
					theme: 'light'
				}
			},
			{
				path: 'survey-management/:id',
				name: 'surveyManagementForm',
				component: () => import('@/drc/pages/controlPanel/survey-management/SurveyManagementForm'),
				meta:{
					headerColor: 'dark',
					sideMenuColor: 'dark',
					backgroundColor: 'blue',
					theme: 'light'
				}
			},
			{
				path: 'video-management',
				name: 'videoManagement',
				component: () => import('@/drc/pages/controlPanel/video-management/VideoManagement.vue'),
				meta:{
					headerColor: 'dark',
					sideMenuColor: 'dark',
					backgroundColor: 'blue',
					theme: 'light'
				}
			},
			{
				path: 'video-management/:id',
				name: 'videoManagementForm',
				component: () => import('@/drc/pages/controlPanel/video-management/VideoManagementForm.vue'),
				meta:{
					headerColor: 'dark',
					sideMenuColor: 'dark',
					backgroundColor: 'blue',
					theme: 'light'
				}
			},
			{
				path: 'resource-file-management',
				name: 'resourcesManagement',
				component: () => import('@/drc/pages/controlPanel/resources-management/ResourcesManagement.vue'),
				meta:{
					headerColor: 'dark',
					sideMenuColor: 'dark',
					backgroundColor: 'blue',
					theme: 'light'
				}
			},
			{
				path: 'resource-file-management/:id',
				name: 'resourcesManagementForm',
				component: () => import('@/drc/pages/controlPanel/resources-management/ResourcesManagementForm.vue'),
				meta:{
					headerColor: 'dark',
					sideMenuColor: 'dark',
					backgroundColor: 'blue',
					theme: 'light'
				}
			},
			{
				path: 'promotions-management',
				name: 'promotionsManagement',
				component: () => import('@/drc/pages/controlPanel/promotions-management/PromotionsManagement.vue'),
				meta:{
					headerColor: 'dark',
					sideMenuColor: 'dark',
					backgroundColor: 'blue',
					theme: 'light'
				}
			},
		],
		meta: {
			permissions: [PERMISSIONS.additionalCmtAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark',
			backgroundColor: 'blue',
			theme: 'light'
		},
	},
	{
		path: '/analytics',
		name: 'analytics',
		component: () => import('@/drc/pages/controlPanel/analytics/Analytics.vue'),
		meta:{
			headerColor: 'dark',
			sideMenuColor: 'dark',
			backgroundColor: 'blue',
			theme: 'dark'
		}
	},
	{
		path: '/subscriptions/form',
		name: "subscription-form",
		component: () => import("@/drc/pages/subscriptions/SubscriptionForm"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark',
			backgroundColor: 'light'
		}
	},
	{
		path: '/subscriptions/form/:id',
		name: "subscription-form",
		component: () => import("@/drc/pages/subscriptions/SubscriptionForm"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark',
			backgroundColor: 'light'
		}
	},
	{
		path: '/my-subscriptions',
		name: "my-subscriptions",
		component: () => import("@/drc/pages/subscriptions/MySubscriptions"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark',
			backgroundColor: 'light'
		}
	},
	{
		path: '/subscriptions/:id/',
		name: "subscription-report",
		component: () => import("@/drc/pages/subscriptions/SubscriptionReport"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark',
			backgroundColor: 'light'
		}
	},
	{
		path: '/subscriptions/:id/:subscriptionHistoryId',
		name: "subscription-report",
		component: () => import("@/drc/pages/subscriptions/SubscriptionReport"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark',
			backgroundColor: 'light'
		}
	},

	// ------- Project CRUD
	{
		path: '/submission/new-project-form',
		name: "submission-new-project-form",
		component: () => import('@/drc/pages/submit-form/SubmitForm'),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark'
		}
	},
	{
		path: '/submission/continue-draft-project/:id',
		name: "submission-continue-draft-project",
		component: () => import('@/drc/pages/submit-form/SubmitForm'),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark'
		}
	},
	{
		path: `/${PAGE_NAME.submission}/${PAGE_NAME.projects}/:id/${PAGE_NAME.editLanguage}/:langId`,
		name: `${PAGE_NAME.submission}_${PAGE_NAME.projects}_${PAGE_NAME.editLanguage}`,
		component: () => import("@/drc/pages/project/translation-validation/TranslationValidationForm.vue"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark',
			backgroundColor: 'light',
			theme: 'light'
		}
	},
	{
		path: `/${PAGE_NAME.manage}/${PAGE_NAME.content}/:stepName/form`,
		name: "management-form",
		component: () => import('@/drc/pages/management-form/ManagementForm'),
		meta: {
			permissions: [PERMISSIONS.additionalCmtAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark'
		}
	},

	// ------------------------------------ User Dashboard
	{
		path: `/my-stories`,
		name: 'my-stories',
		component: () => import("@/drc/pages/my-stories/MyStories.vue"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark'
		},
	},
	{
		path: `/my-stories/saved`,
		name: 'my-stories-saved',
		component: () => import("@/drc/pages/my-stories/MyStoriesResults.vue"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark',
			backgroundColor: 'light',
			theme: 'light'
		}
	},
	{
		path: `/my-stories/drafts`,
		name: 'my-stories-drafts',
		component: () => import("@/drc/pages/my-stories/MyStoriesResults.vue"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark',
			backgroundColor: 'light',
			theme: 'light'
		}
	},
	{
		path: `/my-stories/shared`,
		name: 'my-stories-shared',
		component: () => import("@/drc/pages/my-stories/MyStoriesResults.vue"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark',
			backgroundColor: 'light',
			theme: 'light'
		}
	},

	{
		path: `/my-stories/for-approval`,
		name: 'my-stories-for-approval',
		component: () => import("@/drc/pages/my-stories/MyStoriesResults.vue"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark',
			backgroundColor: 'light',
			theme: 'light'
		}
	},

	{
		path: `/my-stories/cmt-review`,
		name: 'my-stories-cmt-review',
		component: () => import("@/drc/pages/my-stories/MyStoriesResults.vue"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark',
			backgroundColor: 'light',
			theme: 'light'
		}
	},

	{
		path: `/my-stories/archived`,
		name: 'my-stories-archived',
		component: () => import("@/drc/pages/my-stories/MyStoriesResults.vue"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark',
			backgroundColor: 'light',
			theme: 'light'
		}
	},


	// ------------------------------------ RESOURCES
	// {
	// 	path: `/resources`,
	// 	name: "resources-page",
	// 	component: () => import("@/drc/pages/faq/FAQPage.vue"),
	// 	meta: {
	// 		permissions: [PERMISSIONS.defaultAccess]
	// 	}
	// },
	{
		path: `/resources`,
		name: "resources-page",
		component: () => import("@/drc/pages/faq/ResourcePage"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'dark',
			sideMenuColor: 'dark'
		}
	},
	{
		path: '/faq',
		redirect: '/resources',
	},

	// ------------------------------------ GOP
	// OLD DESIGN
	// {
	// 	path: `/gop/create-offer`,
	// 	name: "gop-submission-form-page",
	// 	component: () => import("@/gop/pages/createOffer/CreateOffer.vue"),
	// 	meta: {
	// 		headerColor: 'light',
	// 		sideMenuColor: 'light'
	// 	}
	// },

	// OLD DESIGN
	// {
	// 	path: `/gop/create-sector-presentation`,
	// 	name: "gop-sector-presentation-submission-form-page",
	// 	component: () => import("@/gop/pages/createSectorPresentation/CreateSectorPresentation.vue"),
	// 	meta: {
	// 		headerColor: 'light',
	// 		sideMenuColor: 'light'
	// 	}
	// },

	//OLD DESIGN
	// {
	// 	path: `/gop/create-recognition`,
	// 	name: "gop-recognition-form-page",
	// 	component: () => import("@/gop/pages/createRecognition/CreateRecognition.vue"),
	// 	meta: {
	// 		headerColor: 'light',
	// 		sideMenuColor: 'light'
	// 	}
	// },

	//OLD DESIGN
	// {
	// 	path: `/gop/home`,
	// 	name: "gop-home-page",
	// 	component: () => import("@/gop/pages/home/GopHome"),
	// 	meta: {
	// 		permissions: [PERMISSIONS.defaultAccess],
	// 		headerColor: 'light',
	// 		sideMenuColor: 'light'
	// 	}
	// },

	// {
	// 	path: `/gop/offers/:id`,
	// 	name: `gop-single-offer`,
	// 	component: () => import("@/gop/pages/offer/ItemOffer"),
	// 	meta: {
	// 		permissions: [PERMISSIONS.defaultAccess],
	// 		headerColor: 'light',
	// 		sideMenuColor: 'light'
	// 	}
	// },

	//OLD DESIGN
	{
		path: `/gop/sector-presentations/:id`,
		redirect: `/gop/sectors/:id`
		// name: `sector-presentation`,
		// component: () => import("@/gop/pages/offer/ItemOffer"),
		// meta: {
		// 	permissions: [PERMISSIONS.defaultAccess],
		// 	headerColor: 'light',
		// 	sideMenuColor: 'light'
		// }
	},

	//OLD DESIGN
	// {
	// 	path: `/gop/recognitions`,
	// 	name: `gop-recognitions`,
	// 	component: () => import("@/gop/pages/recognitions/RecognitionsList"),
	// 	meta: {
	// 		permissions: [PERMISSIONS.defaultAccess],
	// 		headerColor: 'light',
	// 		sideMenuColor: 'light'
	// 	}
	// },

	// OLD DESIGN
	// {
	// 	path: `/gop/recognitions/:id`,
	// 	name: `gop-single-recognition`,
	// 	component: () => import("@/gop/pages/offer/ItemOffer"),
	// 	meta: {
	// 		permissions: [PERMISSIONS.defaultAccess],
	// 		headerColor: 'light',
	// 		sideMenuColor: 'light'
	// 	}
	// },

	//OLD DESIGN
	// {
	// 	path: `/gop/search`,
	// 	name: "gop-offers-page",
	// 	component: () => import("@/gop/pages/offers/Offers"),
	// 	meta: {
	// 		permissions: [PERMISSIONS.defaultAccess],
	// 		headerColor: 'light',
	// 		sideMenuColor: 'light'
	// 	}
	// },
	{
		path: `/gop/pdf-preview`,
		name: "pdf-preview-page",
		component: () => import("@/gop/pages/offer/PdfPreview"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'light',
			sideMenuColor: 'light'
		}
	},

	//NEW DESIGN
	{
		path: '/gop/search',
		name: 'GopCommonSearch',
		component: () => import('@/gop-redesign/pages/search-page/GopCommonSearch.vue'),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'light',
			sideMenuColor: 'light',
			isGopRedesign: true,
			backgroundColor: 'white'
		}
	},

	//NEW DESIGN
	{
		path: '/gop/home',
		name: 'GopHomePage',
		component: () => import('@/gop-redesign/pages/home/GopHomePage.vue'),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'light',
			sideMenuColor: 'light',
			isGopRedesign: true,
			backgroundColor: 'white'
		}
	},

	// NEW DESIGN
	{
		path: `/gop/create-sector-presentation`,
		name: "gop-sector-presentation-submission-form-page",
		component: () => import("@/gop/pages/createSectorPresentation/CreateSectorPresentation.vue"),
		meta: {
			headerColor: 'light',
			sideMenuColor: 'light',
			backgroundColor: 'white'
		}
	},

	//NEW DESIGN
	{
		path: `/gop/create-offer`,
		name: "gop-submission-form-page",
		component: () => import("@/gop/pages/createOffer/CreateOffer.vue"),
		meta: {
			headerColor: 'light',
			sideMenuColor: 'light',
			backgroundColor: 'white'
		}
	},

	//NEW DESIGN
	{
		path: '/gop/recognitions',
		name: 'RecognitionSearch',
		component: () => import('@/gop-redesign/pages/recognitions/RecognitionSearch.vue'),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'light',
			sideMenuColor: 'light',
			isGopRedesign: true,
			backgroundColor: 'white'
		}
	},


	//NEW DESIGN
	{
		path: '/gop/recognitions/:id',
		name: 'RecognitionView',
		component: () => import('@/gop-redesign/pages/view/RecognitionView.vue'),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'light',
			sideMenuColor: 'light',
			isGopRedesign: true,
			backgroundColor: 'white',
			prev: null
		}
	},

	//NEW DESIGN
	{
		path: '/gop/offers/:id',
		name: 'OfferView',
		component: () => import('@/gop-redesign/pages/view/OfferView.vue'),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'light',
			sideMenuColor: 'light',
			isGopRedesign: true,
			backgroundColor: 'white',
			prev: null
		}
	},

	//NEW DESIGN
	{
		path: '/gop/sectors/:id',
		name: 'SectorView',
		component: () => import('@/gop-redesign/pages/view/SectorView.vue'),
		meta: {
		permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'light',
			sideMenuColor: 'light',
			isGopRedesign: true,
			backgroundColor: 'white'
		}
	},

	//NEW DESIGN
	{
		path: `/gop/create-recognition`,
		name: "gop-new-recognition-form-page",
		component: () => import("@/gop-redesign/pages/createRecognition/CreateRecognition.vue"),
		meta: {
			permissions: [PERMISSIONS.defaultAccess],
			headerColor: 'light',
			sideMenuColor: 'light',
			isGopRedesign: true,
			backgroundColor: 'white'
		}
	},

	//NEW DESIGN - NOT NEEDED
	// {
	// 	path: '/redesign/gop/popup-test',
	// 	name: 'popupTest',
	// 	component: () => import('@/gop-redesign/pages/PopupTest.vue'),
	// 	meta: {
	// 		permissions: [PERMISSIONS.defaultAccess],
	// 		headerColor: 'light',
	// 		sideMenuColor: 'light',
	// 		isGopRedesign: true
	// 	}
	// },

	//NEW DESIGN - NOT NEEDED
	{
		path: '/redesign/gop/404',
		redirect: ErrorPage
		// name: 'gopError',
		// component: () => import('@/gop-redesign/pages/Error.vue'),
		// meta: {
		// 	backgroundColor: 'white',
		// 	theme: 'light',
		// 	headerColor: 'light',
		// 	sideMenuColor: 'light',
		// 	isGopRedesign: true
		// }
	},


	// ------------------------------------ 404 - Error
	{
		path: "*",
		component: ErrorPage,
		meta: {
			headerColor: 'dark',
			sideMenuColor: 'dark',
			backgroundColor: 'blue',
			theme: 'light'
		}
	},
];

/*
const routes = [
  {
    path: "*",
    component: () => import("../pages/underConstruction/UnderConstruction.vue"),
  },
]
*/

const scrollBehavior = function (to) {
	const position = {}
	if (to.hash) {
		position.selector = to.hash;

		if (document.querySelector(to.hash)) {
			return new Promise((resolve) => {
				setTimeout(() => {
					resolve(position);
				}, 500);
			})
		}
	}

	return {x: 0, y: 0};
};

const router = new VueRouter({
	mode: "history",
	base: process.env.BASE_URL,
	routes,
	scrollBehavior
});

router.onError((error) => {
	if (error.name === 'ChunkLoadError') {
		window.location.reload();
	}
});

let previousRoute = null;
router.beforeEach((to, from, next) => {
	previousRoute = from;
	router.previousRoute = previousRoute;
	const isLoginPage = to.name === 'login' || to.name === 'sign-in' || to.name === 'register'
		|| to.name === 'forgot-password' || to.name === 'confirmation' || to.name === 'login-options'
		|| to.name === 'reset-password' || to.name === 'register-success';


	let jwt = localStorage.getItem('jwt');

	if (!isLoginPage && !jwt) {
		console.warn(`Redirected after login to: ${to.fullPath}`)
		if(!to.fullPath.includes('/logout')){
			localStorage.setItem('redirectToUrlAfterLogin', to.fullPath)
		}
		next('/');
	} else if (isLoginPage && !jwt) {
		next();
	} else if (jwt && !isLoginPage) {
		next();
	} else if (jwt && isLoginPage) {
		next('/landing');
	}

});

router.beforeResolve((to, from, next) => {
	const fromId = from.params.id;
	const toId = to.params.id;

	let hasPermissionVal = '';

	if (to.meta.permissions !== undefined) {
		hasPermissionVal = hasPermission(to.meta.permissions);
	} else {
		hasPermissionVal = true;
	}

	if (hasPermissionVal) {
		if (fromId && toId && fromId !== toId) {
			next();
			EventBusNavbar.$emit("url-param-id-changed", toId);
			EventCurdSideMenu.$emit('close-side-menu');
		} else {
			next();
			EventCurdSideMenu.$emit('close-side-menu');
		}
	} else {
		next('/404');
	}
});


function hasPermission(routePermissions) {
	if (routePermissions && routePermissions.length > 0) {
		let permissions = store.getters.getUser.permissions;
		let jwt = localStorage.getItem('jwt');
		if (permissions.length === 0 && jwt) {
			return true;
		} else {
			return store.getters.getUser.permissions.some(permission => routePermissions.includes(permission));
		}
	}

	return true;
}

export default router;
